import { ChannelHelpers } from '.';
import * as Types from '../constants/Types';
import { EventModelType } from '../constants/v3';
import { isPhoneChannelTypeId, isMessageChannel } from './ChannelHelpers';

function isSecureChannel(secureChannel) {
  if (!secureChannel) return false;
  return (secureChannel.typeId === Types.TYPE_SECURE && !secureChannel.deleted);
}

/**
 * Pure function that resolves inbox communication context (from/to channels).
 * Extracted from useInboxCommunicationContext so it can be called from both
 * the hook (React context) and thunks (Redux context) without duplicating logic.
 */
export default function resolveInboxCommunicationContext({
  activeUser,
  loggedInUser,
  userOrganization,
  channels,
  groups,
  threadFromChannelIds,
  phones,
  facebooks,
  instagrams,
  rhinograms,
  mostRecentEvent,
  mostRecentMessageEvent,
  mostRecentSecureEvent,
  groupId,
  isAssignedInbox,
}) {
  let activeToChannelId;
  let activeFromChannelId;
  let activeSecureFromChannelId;

  if (!activeUser) return null;
  const phonesSubset = phones.filter((phone) => activeUser.phones?.includes(phone.id));
  const activeUserOwnedPhones = phonesSubset.filter((phone) => phone.ownerId === activeUser.id && !phone.hasOptedOut);
  if (mostRecentMessageEvent) {
    const mostRecentChannel = channels[mostRecentMessageEvent.channelId];
    if (mostRecentChannel.typeId === Types.TYPE_SMS && !mostRecentMessageEvent?.from && !mostRecentMessageEvent?.externalEndpoint?.id) {
      if (activeUserOwnedPhones.length) {
        const channelPhone = phones.find((phone) => phone.id === mostRecentChannel.details?.phone);
        if (channelPhone?.value && activeUserOwnedPhones.length) {
          mostRecentMessageEvent.from = channelPhone.value; // eslint-disable-line no-param-reassign
        }
      } else return null;
    }
    // to
    if (mostRecentMessageEvent.from) {
      const activeToChannel = activeUserOwnedPhones.find((phone) => phone?.value === mostRecentMessageEvent.from);
      activeToChannelId = activeToChannel?.id;
      if (activeToChannelId) activeToChannelId = `sms-${activeToChannelId}`;
    } else if (mostRecentMessageEvent.phoneId) {
      activeToChannelId = `sms-${mostRecentMessageEvent.phoneId}`;
    } else if (mostRecentChannel.typeId === Types.TYPE_SMS && mostRecentMessageEvent?.externalEndpoint) {
      activeToChannelId = `sms-${mostRecentMessageEvent?.externalEndpoint?.id}`;
    } else if (mostRecentChannel.typeId === Types.TYPE_FACEBOOK) {
      activeToChannelId = activeUser.facebooks.find((id) => facebooks[id]?.value === mostRecentMessageEvent.facebookExternalId);
      if (!activeToChannelId) activeToChannelId = activeUser.facebooks.find((id) => id === mostRecentMessageEvent.facebookId);
      if (!activeToChannelId) activeToChannelId = activeUser.facebooks.find((id) => facebooks[id].channelId === mostRecentChannel.id);
      if (activeToChannelId) activeToChannelId = `facebook-${activeToChannelId}`;
    } else if (mostRecentChannel.typeId === Types.TYPE_INSTAGRAM) {
      activeToChannelId = activeUser.instagrams.find((id) => instagrams[id]?.value === mostRecentMessageEvent.instagramExternalId);
      if (!activeToChannelId) activeToChannelId = activeUser.instagrams.find((id) => id === mostRecentMessageEvent.instagramId);
      if (!activeToChannelId) activeToChannelId = activeUser.instagrams.find((id) => instagrams[id].channelId === mostRecentChannel.id);
      if (activeToChannelId) activeToChannelId = `instagram-${activeToChannelId}`;
    } else if (mostRecentChannel?.typeId === Types.TYPE_CLASS_RHINOGRAM) {
      if (!activeToChannelId) activeToChannelId = activeUser.rhinograms.find((id) => rhinograms[id].channelId === mostRecentChannel.id);
      if (activeToChannelId) activeToChannelId = `rhinogram-${activeToChannelId}`;
    }
    // from
    if (mostRecentChannel && threadFromChannelIds.includes(mostRecentChannel.id) && isMessageChannel(mostRecentChannel)) {
      activeFromChannelId = mostRecentChannel.id;
    }
    if (mostRecentEvent?.isComplete && threadFromChannelIds.find((id) => channels[id].isDefault === 1 && !channels[id].deleted)) {
      activeFromChannelId = threadFromChannelIds.find((id) => channels[id].isDefault === 1 && !channels[id].deleted);
    }
  }

  if (mostRecentSecureEvent) {
    const mostRecentSecureChannel = channels[mostRecentSecureEvent.channelId];
    if (mostRecentSecureChannel
      && threadFromChannelIds.includes(mostRecentSecureChannel.id)
      && mostRecentSecureChannel.typeId === Types.TYPE_SECURE
      && !mostRecentSecureChannel.deleted
    ) {
      activeSecureFromChannelId = mostRecentSecureChannel.id;
    }
  }

  // Showing member's default channel everytime for Assign to Me inbox.
  if (isAssignedInbox && loggedInUser?.defaultChannelId && threadFromChannelIds.includes(loggedInUser?.defaultChannelId)) {
    const { defaultChannelId } = loggedInUser;
    const channel = channels[defaultChannelId];
    if (channel) {
      if (isMessageChannel(channel)) activeFromChannelId = defaultChannelId;
    }
  }

  // all route
  if (!groupId && loggedInUser?.defaultChannelId && threadFromChannelIds.includes(loggedInUser?.defaultChannelId) && (mostRecentEvent?.isComplete || !mostRecentEvent)) {
    const { defaultChannelId } = loggedInUser;
    const channel = channels[defaultChannelId];
    if (channel) {
      if (isMessageChannel(channel)) activeFromChannelId = defaultChannelId;
      else if (isSecureChannel(channel)) activeSecureFromChannelId = defaultChannelId;
    }
  }

  // default if we don't have channels set by this point
  if (!activeSecureFromChannelId) activeSecureFromChannelId = threadFromChannelIds.find((id) => channels[id].typeId === Types.TYPE_SECURE && !channels[id].deleted);
  if (!activeToChannelId && activeUserOwnedPhones.length > 0) {
    const toCell = activeUserOwnedPhones.find((phone) => phone && phone.typeId === Types.TYPE_CELL);
    activeToChannelId = `sms-${toCell?.id || activeUserOwnedPhones[0]?.id}`;
  }
  if (!activeToChannelId && activeUser.facebooks?.length > 0) activeToChannelId = `facebook-${activeUser.facebooks[0]}`;
  if (!activeToChannelId && activeUser.instagrams?.length > 0) activeToChannelId = `instagram-${activeUser.instagrams[0]}`;
  if (!activeToChannelId && activeUser.rhinograms?.length > 0) activeToChannelId = `rhinogram-${activeUser.rhinograms[0]}`;
  const toCommunicationId = activeToChannelId && ChannelHelpers.getCommunicationId(activeToChannelId);
  const toChannelClass = activeToChannelId ? ChannelHelpers.getChannelClass(activeToChannelId) : null;

  if (groupId) {
    if (!activeSecureFromChannelId) {
      if (!activeToChannelId && activeUserOwnedPhones.length > 0) {
        const toCell = activeUserOwnedPhones.find((phone) => phone && phone.typeId === Types.TYPE_CELL);
        activeToChannelId = `sms-${toCell.id || activeUserOwnedPhones[0]?.id}`;
      }
      activeSecureFromChannelId = threadFromChannelIds.find((id) => (
        channels[id].typeId === Types.TYPE_SECURE
        && channels[id].route.groupId === groupId
        && !channels[id].deleted
      ));
    }

    if (userOrganization?.isDefaultGroupOutboundChannelEnabled) {
      const routedChannelId = threadFromChannelIds.find((id) => (
        isPhoneChannelTypeId(channels[id].typeId)
        && channels[id].route.groupId === groupId
        && !channels[id].deleted
      ));

      if (groups[groupId]?.defaultChannelId &&
        (mostRecentEvent?.isComplete || !mostRecentEvent || mostRecentEvent.modelType !== EventModelType.Message ||
          (mostRecentEvent.modelType === EventModelType.Message && routedChannelId !== mostRecentEvent?.channelId))) {
        const { defaultChannelId } = groups[groupId];
        const channel = channels[defaultChannelId];

        if (channel) {
          if (isMessageChannel(channel)) activeFromChannelId = defaultChannelId;
        }
      }
    }

    if (!activeFromChannelId && activeUserOwnedPhones.length > 0) {
      activeFromChannelId = threadFromChannelIds.find((id) => (
        isPhoneChannelTypeId(channels[id].typeId)
        && channels[id].route.groupId === groupId
        && !channels[id].deleted
      ));
    }
    if (!activeFromChannelId && toChannelClass === 'facebook') {
      activeFromChannelId = threadFromChannelIds?.find((id) => (
        channels[id].typeId === Types.TYPE_FACEBOOK
        && channels[id].route.groupId === groupId
        && !channels[id].deleted
        && toCommunicationId
        && facebooks[toCommunicationId]
        && facebooks[toCommunicationId].channelId === id
      ));
    }

    if (!activeFromChannelId && toChannelClass === 'instagram') {
      activeFromChannelId = threadFromChannelIds.find((id) => (
        channels[id].typeId === Types.TYPE_INSTAGRAM
        && channels[id].route.groupId === groupId
        && !channels[id].deleted
        && toCommunicationId
        && instagrams[toCommunicationId]
        && instagrams[toCommunicationId].channelId === id
      ));
    }
    if (!activeFromChannelId && toChannelClass === 'rhinogram') {
      activeFromChannelId = threadFromChannelIds.find((id) => (
        channels[id].typeId === Types.TYPE_RHINOGRAM
        && channels[id].route.groupId === groupId
        && !channels[id].deleted
      ));
    }
  }
  // Try for default channel then landline, then sms, then facebook
  if (!activeFromChannelId && activeUserOwnedPhones.length > 0) {
    if (!activeFromChannelId) activeFromChannelId = threadFromChannelIds.find((id) => channels[id].isDefault === 1 && !channels[id].deleted);
    if (!activeFromChannelId) activeFromChannelId = threadFromChannelIds.find((id) => channels[id].typeId === Types.TYPE_SMS && !channels[id].deleted);
    if (!activeFromChannelId) activeFromChannelId = threadFromChannelIds.find((id) => channels[id].typeId === Types.TYPE_TWILIO && !channels[id].deleted);
  }

  if (!activeFromChannelId && toChannelClass === 'facebook') {
    activeFromChannelId = threadFromChannelIds.find((id) => (
      channels[id].typeId === Types.TYPE_FACEBOOK
      && !channels[id].deleted
      && toCommunicationId
      && facebooks[toCommunicationId]
      && facebooks[toCommunicationId].channelId === id
    ));
  }

  if (!activeFromChannelId && toChannelClass === 'instagram') {
    activeFromChannelId = threadFromChannelIds.find((id) => (
      channels[id].typeId === Types.TYPE_INSTAGRAM
      && !channels[id].deleted
      && toCommunicationId
      && instagrams[toCommunicationId]
      && instagrams[toCommunicationId].channelId === id
    ));
  }
  if (!activeFromChannelId && toChannelClass === 'rhinogram') {
    activeFromChannelId = threadFromChannelIds.find((id) => (
      channels[id].typeId === Types.TYPE_RHINOGRAM
      && !channels[id].deleted
    ));
  }

  let activeSecureNotificationToChannelId = toChannelClass === 'sms' ? activeToChannelId : null;
  if (!activeSecureNotificationToChannelId && activeUserOwnedPhones.length > 0) {
    const toCell = activeUserOwnedPhones.find((phone) => phone && phone.typeId === Types.TYPE_CELL);
    activeSecureNotificationToChannelId = `sms-${toCell?.id || activeUserOwnedPhones[0].id}`;
  }

  let activeSecureNotificationFromChannelId =
    activeFromChannelId && isPhoneChannelTypeId(channels[activeFromChannelId].typeId) ?
      activeFromChannelId :
      userOrganization.defaultChannelId;

  if (!activeSecureNotificationFromChannelId && activeUserOwnedPhones.length > 0) {
    if (!activeSecureNotificationFromChannelId) {
      activeSecureNotificationFromChannelId =
        threadFromChannelIds.find((id) => channels[id].isDefault === 1 && !channels[id].deleted);
    }
    if (!activeSecureNotificationFromChannelId) {
      activeSecureNotificationFromChannelId =
        threadFromChannelIds.find((id) => channels[id].typeId === Types.TYPE_SMS && !channels[id].deleted);
    }
    if (!activeSecureNotificationFromChannelId) {
      activeSecureNotificationFromChannelId =
        threadFromChannelIds.find((id) => channels[id].typeId === Types.TYPE_TWILIO && !channels[id].deleted);
    }
  }

  return {
    activeToChannelId,
    activeFromChannelId,
    activeSecureFromChannelId,
    activeSecureNotificationToChannelId,
    activeSecureNotificationFromChannelId,
  };
}
